import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from './AuthContext';
import AudioTranscriptionAnimation from './AudioTranscriptionAnimation';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useI18n } from '../i18n';

// Check if Google OAuth is configured
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const isGoogleOAuthConfigured = Boolean(GOOGLE_CLIENT_ID && GOOGLE_CLIENT_ID.length > 10);

// Check if Email/Password auth is enabled
const isEmailPasswordEnabled = process.env.REACT_APP_ENABLE_EMAIL_PASSWORD_AUTH === 'true';

// Debug: Log the client ID status (remove in production)
console.log('Google OAuth configured:', isGoogleOAuthConfigured, 'Client ID:', GOOGLE_CLIENT_ID?.substring(0, 20) + '...');
console.log('Email/Password auth enabled:', isEmailPasswordEnabled);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// Google Sign-In Button Component (only rendered when OAuth is configured)
const GoogleSignInButton = ({ onSuccess, onError, disabled }) => {
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(false);

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsLoading(true);
      try {
        await onSuccess(tokenResponse.access_token);
      } catch (err) {
        onError(err?.message || t('Failed to sign in with Google. Please try again.'));
      } finally {
        setIsLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google login error:', error);
      onError(t('Google sign-in was cancelled or failed. Please try again.'));
    },
  });

  return (
    <button
      onClick={() => googleLogin()}
      disabled={disabled || isLoading}
      className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl font-bold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
    >
      {isLoading ? (
        <>
          <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          {t('Signing in...')}
        </>
      ) : (
        <>
          <GoogleIcon />
          {t('Continue with Google')}
        </>
      )}
    </button>
  );
};

// Disabled Google Button (when OAuth is not configured)
const GoogleSignInButtonDisabled = () => {
  const { t } = useI18n();
  return (
    <button
      disabled
      className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-slate-100 border-2 border-slate-200 rounded-2xl font-bold text-slate-400 cursor-not-allowed"
      title={t('Google Sign-In is not configured')}
    >
      <GoogleIcon />
      {t('Continue with Google')}
    </button>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err?.message || t('Failed to sign in. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (accessToken) => {
    await loginWithGoogle(accessToken);
    navigate('/dashboard', { replace: true });
  };

  const handleGoogleError = (errorMessage) => {
    setError(errorMessage);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col lg:flex-row font-sans">
      {/* Mobile FOMO Banner - Sticky at top */}
      <div className="viewport-sticky-header lg:hidden sticky top-0 z-50 bg-gradient-to-r from-[#f73140] via-[#ff4757] to-[#f73140] animate-gradient-x">
        <div className="px-4 py-3">
          <div className="flex items-center justify-center gap-2 text-white">
            <svg className="w-5 h-5 flex-shrink-0 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
            </svg>
            <div className="text-center">
              <p className="text-sm font-bold">
                {t('🎉 Limited Time: Get 1 FREE Video!')}
              </p>
              <p className="text-xs font-medium opacity-90">
                {t('No credit card needed')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#f73140] rounded-full blur-[200px] opacity-20 -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600 rounded-full blur-[150px] opacity-10 translate-y-1/2 -translate-x-1/3" />
        </div>
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div>
            <img
              src={`${process.env.PUBLIC_URL}/Logo-ArabCap-Dark.svg`}
              alt="ArabCap"
              className="h-12 w-auto"
            />
          </div>
          <div className="space-y-6">
            <h1 className="text-4xl xl:text-5xl font-extrabold text-white leading-tight tracking-tight">
              {t('AI-Powered Subtitles')}<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f73140] to-orange-400 leading-normal">
                {t('for Arabic Dialects')}
              </span>
            </h1>
            <p className="text-lg text-slate-400 max-w-md leading-relaxed">
              {t('Darija, Masri, Khaleeji — we understand how you really speak. Create beautiful, accurate subtitles in seconds.')}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex -space-x-3 rtl:space-x-reverse">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-10 h-10 rounded-full bg-slate-700 border-2 border-slate-800 flex items-center justify-center text-xs font-bold text-slate-300"
                >
                  {['🇲🇦', '🇪🇬', '🇸🇦', '🇱🇧'][i - 1]}
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-400">
              <span className="font-bold text-white">{t('400M+')}</span> {t('Arabic speakers worldwide')}
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-8 lg:py-12 lg:px-20">
        <div className="w-full max-w-md mx-auto">
          <div className="flex justify-end mb-4 lg:mb-8">
            <LanguageSwitcher compact />
          </div>

          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 flex justify-center">
            <img
              src={`${process.env.PUBLIC_URL}/Logo-ArabCap.svg`}
              alt="ArabCap"
              className="h-10 w-auto"
            />
          </div>

          <div className="mb-6">
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">{t('Welcome back')}</h2>
            <p className="mt-2 text-slate-500">
              {t("Don't have an account?")}{' '}
              <Link to="/signup" className="font-bold text-[#f73140] hover:text-[#d62331] transition-colors">
                {t('Sign up for free')}
              </Link>
            </p>
          </div>

          {/* Audio animation - demo */}
          <div className="lg:hidden mb-8">
            <AudioTranscriptionAnimation />
          </div>

          {/* Google Button */}
          {isGoogleOAuthConfigured ? (
            <GoogleSignInButton
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              disabled={isLoading}
            />
          ) : (
            <GoogleSignInButtonDisabled />
          )}

          {/* Email/Password Login Form */}
          {isEmailPasswordEnabled && (
            <>
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#f8fafc] text-slate-500 font-medium">{t('or continue with email')}</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-2">
                    {t('Email')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    className="w-full px-4 py-4 bg-white border-2 border-slate-200 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#f73140] focus:ring-4 focus:ring-[#f73140]/10 transition-all"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-bold text-slate-700 mb-2">
                    {t('Password')}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full px-4 py-4 bg-white border-2 border-slate-200 rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#f73140] focus:ring-4 focus:ring-[#f73140]/10 transition-all pe-12"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute end-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-[#f73140] to-[#d62331] text-white font-bold py-4 px-6 rounded-2xl hover:from-[#d62331] hover:to-[#c01f2d] focus:outline-none focus:ring-4 focus:ring-[#f73140]/30 transition-all shadow-lg shadow-[#f73140]/20 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('Signing in...')}
                    </div>
                  ) : (
                    t('Sign in')
                  )}
                </button>
              </form>
            </>
          )}

          {error && (
            <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-4 text-sm">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-semibold text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <p className="mt-8 text-center text-xs text-slate-400">
            {t('By continuing, you agree to our')}{' '}
            <a href="/terms" className="underline hover:text-slate-600">{t('Terms of Service')}</a>
            {t(' and ')}
            <a href="/privacy" className="underline hover:text-slate-600">{t('Privacy Policy')}</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
